import React, { useState, useEffect } from "react";
import { Plus, Trash2, GripVertical, ChevronDown } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { api } from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import ReferencePickerModal from "../Common/ReferencePickerModal";
import { vendableProductsConfig, ingredientProductsConfig, unitsConfig } from "../Common/referenceConfigs";
import AppleSelect from "../Common/AppleSelect";
import Button from "../Common/Button";
import { Modal, ModalFooter, SummaryStrip } from "../../stock/StockUI";
import { inputClass } from "../../ClientsTable/clientFormUI";

const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

// Extracts a UUID from either a plain id, a full object {id: "..."}, or an
// IRI string "/api/units/xxx" — the API sometimes returns nested relations as
// IRIs rather than embedded objects depending on serialization group.
const extractId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return value.id || '';
    if (typeof value === 'string' && value.includes('/')) {
        const parts = value.split('/').filter(Boolean);
        return parts[parts.length - 1] || '';
    }
    return value;
};

// True only for a genuinely embedded relation object (has a name to display).
// The API sometimes returns the same relation as a bare IRI string, or (seen
// on RecipeIngredient.product) an empty array — neither carries a name, so
// treating them as "selected" would render a blank/undefined picker label
// that looks exactly like the selection was lost on save.
const isEmbeddedObject = (value) =>
    value != null && typeof value === 'object' && !Array.isArray(value) && 'name' in value;

const TechnicalFileForm = ({ mode = 'create', file = null, isSubRecipe = false, onClose, isDarkMode }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [totalCost, setTotalCost] = useState(0); // coût matière HT / unité produite
    const [totalCostTtc, setTotalCostTtc] = useState(0); // coût matière TTC / unité produite
    const [margin, setMargin] = useState(0); // marge réelle, HT/HT (jamais HT vs TTC)
    const queryClient = useQueryClient();

    const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        defaultValues: {
            name: file?.name || '',
            description: file?.description || '',
            product_id: extractId(file?.product),
            preparation_time: file?.preparationTime ?? file?.preparation_time ?? 0,
            is_sub_recipe: isSubRecipe,
            // The backend only decrements stock on a sale (and only counts the
            // recipe in the food-cost dashboard) when status === 'active' — it
            // defaults new recipes to 'draft' server-side. This form never
            // exposed that field before, so every recipe created here stayed
            // 'draft' forever and stock silently never decremented. Default
            // new recipes to 'active' so "create a recipe and start selling"
            // works the way this form implies it should; still overridable.
            status: file?.status || 'active',
            yield_quantity: file?.yieldQuantity ?? 1,
            yield_unit_id: extractId(file?.yieldUnit),
            target_margin_percent: file?.targetMarginPercent ?? '',
            ingredients: (file?.ingredients && file.ingredients.length > 0)
                ? file.ingredients.map(ing => ({
                    product_id: extractId(ing.product),
                    quantity: ing.quantity ?? '',
                    consumption_unit_id: extractId(ing.consumptionUnit),
                    loss_rate: ing.lossRate ?? 0,
                    conversion_coefficient: ing.conversionCoefficient ?? 1,
                }))
                : [{ product_id: '', quantity: '', consumption_unit_id: '', loss_rate: 0, conversion_coefficient: 1 }],
            sub_recipe_usages: (file?.subRecipeUsages || []).map(u => ({
                sub_recipe_id: extractId(u.subRecipe),
                quantity: u.quantity ?? '',
                unit_id: extractId(u.unit),
            })),
            steps: file?.steps || [{ order: 1, description: '' }]
        }
    });

    const { fields: ingredientFields, append: appendIngredient, remove: removeIngredient } = useFieldArray({
        control,
        name: "ingredients"
    });

    const { fields: subRecipeFields, append: appendSubRecipe, remove: removeSubRecipe } = useFieldArray({
        control,
        name: "sub_recipe_usages"
    });

    const { fields: stepFields, append: appendStep, remove: removeStep } = useFieldArray({
        control,
        name: "steps"
    });

    const watchIngredients = watch("ingredients");
    const watchSubRecipeUsages = watch("sub_recipe_usages");
    const watchYieldQuantity = watch("yield_quantity");
    const watchTargetMargin = watch("target_margin_percent");

    // Which reference picker modal is currently open: 'vendableProduct',
    // 'yieldUnit', `ingredientProduct-${index}`, or `ingredientUnit-${index}`.
    const [activePicker, setActivePicker] = useState(null);

    // Selected products/units are tracked as full objects (not just the id
    // react-hook-form holds), seeded from the recipe's own embedded relations
    // on edit. This avoids the previous bug where the vendable-product and
    // ingredient selects were plain <AppleSelect> options backed by an unpaginated
    // /products fetch — silently capped at the API's default page size of 10,
    // so most products never showed up and cost calculation broke for any
    // ingredient outside that first page. Selection now goes through
    // ReferencePickerModal (search + real pagination), and the chosen object
    // is kept directly, so display and cost math never depend on whether the
    // product happened to be inside some arbitrary first page.
    const [selectedVendableProduct, setSelectedVendableProduct] = useState(
        !isSubRecipe && isEmbeddedObject(file?.product) ? file.product : null
    );
    const [selectedYieldUnit, setSelectedYieldUnit] = useState(
        isEmbeddedObject(file?.yieldUnit) ? file.yieldUnit : null
    );
    const [ingredientProducts, setIngredientProducts] = useState(() => {
        const map = {};
        (file?.ingredients || []).forEach((ing, i) => { if (isEmbeddedObject(ing.product)) map[i] = ing.product; });
        return map;
    });
    const [ingredientUnits, setIngredientUnits] = useState(() => {
        const map = {};
        (file?.ingredients || []).forEach((ing, i) => { if (isEmbeddedObject(ing.consumptionUnit)) map[i] = ing.consumptionUnit; });
        return map;
    });

    // Some relations come back from the API as a bare IRI/id instead of an
    // embedded object (no name to show). Resolve those individually so the
    // picker still shows the linked product/unit's real name on edit, rather
    // than looking like the selection was lost. Runs once per mount — `file`
    // is only ever set when this form is opened, not while it stays open.
    useEffect(() => {
        let cancelled = false;
        const resolveDisplayNames = async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const headers = { 'X-account-id': accountId.id };

            const resolve = async (endpoint, id, setter) => {
                if (!id) return;
                try {
                    const res = await api.get(`${endpoint}/${id}`, { headers });
                    if (!cancelled) setter(res.data);
                } catch {
                    // Leave the picker showing its placeholder — the id itself
                    // is still correctly carried in the form and will be
                    // resubmitted as-is unless the user explicitly changes it.
                }
            };

            if (!isSubRecipe && file?.product && !isEmbeddedObject(file.product)) {
                await resolve('/products', extractId(file.product), setSelectedVendableProduct);
            }
            if (file?.yieldUnit && !isEmbeddedObject(file.yieldUnit)) {
                await resolve('/units', extractId(file.yieldUnit), setSelectedYieldUnit);
            }
            if (file?.ingredients?.length) {
                await Promise.all(file.ingredients.map((ing, index) => Promise.all([
                    ing.product && !isEmbeddedObject(ing.product)
                        ? resolve('/products', extractId(ing.product), (item) => setIngredientProducts(prev => ({ ...prev, [index]: item })))
                        : null,
                    ing.consumptionUnit && !isEmbeddedObject(ing.consumptionUnit)
                        ? resolve('/units', extractId(ing.consumptionUnit), (item) => setIngredientUnits(prev => ({ ...prev, [index]: item })))
                        : null,
                ])));
            }
        };
        resolveDisplayNames();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Existing sub-recipes, to compose into this recipe (a recipe cannot use
    // itself — excluded below — the backend also rejects deeper cycles).
    const { data: subRecipesData } = useQuery({
        queryKey: ['sub-recipes-for-recipe'],
        queryFn: async () => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/recipes', {
                headers: { 'X-account-id': accountId.id },
                params: { is_sub_recipe: true, itemsPerPage: 200 }
            });
            return response.data;
        }
    });

    const availableSubRecipes = (subRecipesData?.data || []).filter(r => r.id !== file?.id);

    const linkedProduct = selectedVendableProduct;

    // Keeps ingredientProducts/ingredientUnits keyed correctly on removal —
    // otherwise the map entries above the removed index would stay attached
    // to their old (now shifted) index.
    const handleRemoveIngredient = (index) => {
        removeIngredient(index);
        const reindex = (prev) => {
            const next = {};
            Object.entries(prev).forEach(([key, value]) => {
                const idx = Number(key);
                if (idx < index) next[idx] = value;
                else if (idx > index) next[idx - 1] = value;
            });
            return next;
        };
        setIngredientProducts(reindex);
        setIngredientUnits(reindex);
    };

    // Calculate total cost — uses the ingredient's PURCHASE price (purchasePriceHt),
    // not its selling price: a recipe's matière cost must reflect what the
    // ingredient cost to buy, matching RecipeStockEngine::getUnitPrice() server-side.
    // Mirrors RecipeStockEngine::calculateRecipeCost() exactly: ingredient stock
    // quantity = quantity × (1 + lossRate/100) × conversionCoefficient, plus each
    // sub-recipe's own cost prorated by (usage quantity / sub-recipe yield),
    // and the whole thing divided by this recipe's own yield quantity.
    // react-hook-form's watch() can return an array whose reference doesn't
    // change on every keystroke for a nested field-array path, which would
    // silently defeat a useEffect dependency check (Object.is on the array
    // reference). Depend on the serialized content instead so every real
    // change is picked up.
    const ingredientsKey = JSON.stringify(watchIngredients);
    const subRecipeUsagesKey = JSON.stringify(watchSubRecipeUsages);

    // Computes ingredients + sub-recipes cost on a given basis ('purchasePriceHt'
    // or 'purchasePriceTtc' for ingredients, 'cost' or 'costTtc' for sub-recipes),
    // mirroring RecipeStockEngine::calculateRecipeCostByBasis() exactly on both
    // bases so HT and TTC never get mixed.
    const computeCost = (ingredientPriceField, subRecipeCostField) => {
        const ingredientsCost = (watchIngredients || []).reduce((acc, ingredient, index) => {
            if (!ingredient.product_id || !ingredient.quantity) return acc;
            const product = ingredientProducts[index];
            if (!product || product.id !== ingredient.product_id) return acc;
            const unitCost = product[ingredientPriceField] != null ? Number(product[ingredientPriceField]) : 0;
            const lossRate = parseFloat(ingredient.loss_rate) || 0;
            const coefficient = parseFloat(ingredient.conversion_coefficient) || 1;
            const stockQuantity = parseFloat(ingredient.quantity) * (1 + lossRate / 100) * coefficient;
            return acc + (unitCost * stockQuantity);
        }, 0);

        const subRecipesCost = (watchSubRecipeUsages || []).reduce((acc, usage) => {
            if (!usage.sub_recipe_id || !usage.quantity) return acc;
            const subRecipe = availableSubRecipes.find(r => r.id === usage.sub_recipe_id);
            if (!subRecipe) return acc;
            const subYield = Math.max(1, Number(subRecipe.yieldQuantity) || 1);
            const costPerUnit = (Number(subRecipe[subRecipeCostField]) || 0) / subYield;
            return acc + (costPerUnit * parseFloat(usage.quantity));
        }, 0);

        const yieldQty = Math.max(1, parseFloat(watchYieldQuantity) || 1);
        return (ingredientsCost + subRecipesCost) / yieldQty;
    };

    // Product.price is TTC in this system (see clicoo ExportCatalogService /
    // InvoiceGeneratorService) — never compared directly against a HT cost.
    const taxRate = linkedProduct?.taxes?.value != null ? Number(linkedProduct.taxes.value) : 0;
    const priceTtc = linkedProduct?.price != null ? Number(linkedProduct.price) : null;
    const priceHt = priceTtc != null ? priceTtc / (1 + taxRate / 100) : null;
    const targetMarginNum = watchTargetMargin !== '' && watchTargetMargin != null ? parseFloat(watchTargetMargin) : null;
    const recommendedPriceHt = targetMarginNum != null && targetMarginNum < 100 ? totalCost / (1 - targetMarginNum / 100) : null;
    const recommendedPriceTtc = recommendedPriceHt != null ? recommendedPriceHt * (1 + taxRate / 100) : null;

    useEffect(() => {
        if (!watchIngredients) return;

        const costHt = computeCost('purchasePriceHt', 'cost');
        const costTtc = computeCost('purchasePriceTtc', 'costTtc');

        setTotalCost(costHt);
        setTotalCostTtc(costTtc);

        // Marge réelle : toujours HT / HT (jamais HT vs TTC ni l'inverse),
        // matches RecipeController::getFoodCost() (corrigé — Étape 4).
        if (priceHt != null && priceHt > 0) {
            setMargin(((priceHt - costHt) / priceHt) * 100);
        } else {
            setMargin(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ingredientsKey, subRecipeUsagesKey, watchYieldQuantity, linkedProduct, ingredientProducts, subRecipesData]);

    const mutation = useMutation({
        mutationFn: async (data) => {
            const accountId = await secureStorage.getItem("selectedAccount");
            const url = mode === 'edit' ? `/recipes/${file.id}` : '/recipes';
            const method = mode === 'edit' ? 'put' : 'post';

            return api[method](url, data, {
                headers: { 'X-account-id': accountId.id }
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['technical-files']);
            queryClient.invalidateQueries(['sub-recipes']);
            onClose();
        }
    });

    const onSubmit = async (data) => {
        setIsLoading(true);
        try {
            // total_cost/margin are NOT sent: the backend recalculates
            // Recipe.cost itself on every save (RecipeStateProcessor), from
            // the ingredients/sub_recipe_usages payload below — sending a
            // client-computed cost would be a stale duplicate at best.
            await mutation.mutateAsync(data);
        } catch (error) {
            const message = error.response?.data?.detail || error.response?.data?.error || 'Erreur lors de la sauvegarde';
            alert(message);
        } finally {
            setIsLoading(false);
        }
    };

    const isViewMode = mode === 'view';

    const fieldClass = `${inputClass(false)} ${isViewMode ? 'opacity-60' : ''}`;
    // Button that opens a ReferencePickerModal, styled as a field
    const pickerClass = `${inputClass(false)} text-left flex items-center justify-between gap-2 ${
        isViewMode ? 'opacity-60 cursor-not-allowed' : 'hover:border-primary/50'}`;
    const smallFieldClass = `px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${isViewMode ? 'opacity-60' : ''}`;
    const hintClass = "text-xs text-gray-500 dark:text-gray-400 mt-1";
    const sectionClass = "px-4 sm:px-6 py-5 border-b border-gray-200 dark:border-gray-700";

    const title = mode === 'create' ? (isSubRecipe ? 'Nouvelle sous-recette' : 'Nouvelle fiche technique') :
        mode === 'edit' ? 'Modifier' : 'Détails';

    const SectionTitle = ({ children, action }) => (
        <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="section-title">{children}</h3>
            {!isViewMode && action}
        </div>
    );

    return (
        <>
        <Modal
            title={title}
            subtitle={mode !== 'create' ? file?.name : null}
            onClose={onClose}
            size="2xl"
            bodyClass=""
            footer={isViewMode ? (
                <button type="button" onClick={onClose}
                        className="w-full py-4 border-t border-gray-200 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                    Fermer
                </button>
            ) : (
                <ModalFooter onCancel={onClose} onConfirm={handleSubmit(onSubmit)} saving={isLoading}
                             label={isLoading ? 'Enregistrement...' : 'Enregistrer'}/>
            )}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                {/* General Info */}
                <section className={sectionClass}>
                    <SectionTitle>Informations générales</SectionTitle>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                            <label className={labelClass}>Nom *</label>
                            <input {...register("name", { required: true })} disabled={isViewMode} className={fieldClass} />
                            {errors.name && <p className="text-red-500 text-xs mt-1">Nom requis</p>}
                        </div>

                        {!isSubRecipe && (
                            <div>
                                <label className={labelClass}>Produit vendable associé *</label>
                                <button
                                    type="button"
                                    onClick={() => !isViewMode && setActivePicker('vendableProduct')}
                                    disabled={isViewMode}
                                    className={pickerClass}
                                >
                                    <span className={`truncate ${selectedVendableProduct ? '' : 'text-gray-400'}`}>
                                        {selectedVendableProduct ? selectedVendableProduct.name : 'Sélectionner un produit vendable'}
                                    </span>
                                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                </button>
                                <input type="hidden" {...register("product_id", { required: true })} />
                                {errors.product_id && <p className="text-red-500 text-xs mt-1">Un produit vendable doit être associé à la fiche technique</p>}
                                <p className={hintClass}>
                                    Prix de vente : {linkedProduct ? `${Number(linkedProduct.price).toFixed(2)} DH TTC` : '—'} (défini dans Catalogue → Produits)
                                </p>
                            </div>
                        )}

                        {!isSubRecipe && (
                            <div>
                                <label className={labelClass}>Marge cible (%)</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="99"
                                    placeholder="Ex: 30"
                                    {...register("target_margin_percent")}
                                    disabled={isViewMode}
                                    className={fieldClass}
                                />
                                <p className={hintClass}>
                                    Utilisée pour calculer le prix recommandé ci-dessous — n'affecte jamais le prix de vente réel du produit.
                                </p>
                            </div>
                        )}

                        <div>
                            <label className={labelClass}>Statut</label>
                            <AppleSelect {...register("status")} disabled={isViewMode} className={fieldClass}>
                                <option value="draft">Brouillon</option>
                                <option value="active">Active</option>
                                <option value="archived">Archivée</option>
                            </AppleSelect>
                            <p className={hintClass}>
                                Seule une fiche "Active" déclenche le déstockage automatique à la vente et compte dans le tableau de bord food cost.
                            </p>
                        </div>

                        <div>
                            <label className={labelClass}>Temps de préparation (min)</label>
                            <input type="number" {...register("preparation_time")} disabled={isViewMode} className={fieldClass} />
                        </div>

                        <div>
                            <label className={labelClass}>Quantité produite (rendement)</label>
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    step="0.001"
                                    min="0.001"
                                    {...register("yield_quantity", { min: 0.001 })}
                                    disabled={isViewMode}
                                    placeholder="Ex: 1"
                                    className={`${fieldClass} flex-1`}
                                />
                                <div className="flex-1 min-w-0">
                                    <button
                                        type="button"
                                        onClick={() => !isViewMode && setActivePicker('yieldUnit')}
                                        disabled={isViewMode}
                                        className={pickerClass}
                                    >
                                        <span className={`truncate ${selectedYieldUnit ? '' : 'text-gray-400'}`}>
                                            {selectedYieldUnit ? `${selectedYieldUnit.name} (${selectedYieldUnit.code || ''})` : 'Unité de rendement'}
                                        </span>
                                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    </button>
                                    <input type="hidden" {...register("yield_unit_id")} />
                                </div>
                            </div>
                            <p className={hintClass}>
                                Ex : 1 kg de sauce tomate produite — le coût affiché ci-dessous est calculé pour cette quantité.
                            </p>
                        </div>

                        <div className="md:col-span-2">
                            <label className={labelClass}>Description</label>
                            <textarea {...register("description")} disabled={isViewMode} rows={3} className={fieldClass} />
                        </div>
                    </div>
                </section>

                {/* Live Cost & Pricing Calculation — same hairline strip as the Stock totals */}
                <SummaryStrip items={[
                    { key: 'costHt', label: 'Coût matière HT', value: `${totalCost.toFixed(2)} DH`, color: 'text-primary-dark dark:text-primary' },
                    { key: 'costTtc', label: 'Coût matière TTC', value: `${totalCostTtc.toFixed(2)} DH` },
                    ...(!isSubRecipe ? [
                        { key: 'priceHt', label: 'Prix vente HT', value: priceHt != null ? `${priceHt.toFixed(2)} DH` : '—' },
                        { key: 'priceTtc', label: 'Prix vente TTC', value: priceTtc != null ? `${priceTtc.toFixed(2)} DH` : '—' },
                        {
                            key: 'margin', label: 'Marge réelle (HT)',
                            value: linkedProduct ? `${margin.toFixed(2)}%` : '—',
                            color: !linkedProduct ? undefined
                                : margin > 30 ? 'text-green-600 dark:text-green-400'
                                    : margin > 15 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400',
                        },
                        { key: 'recoHt', label: 'Prix recommandé HT', value: recommendedPriceHt != null ? `${recommendedPriceHt.toFixed(2)} DH` : '—' },
                        { key: 'recoTtc', label: 'Prix recommandé TTC', value: recommendedPriceTtc != null ? `${recommendedPriceTtc.toFixed(2)} DH` : '—' },
                    ] : []),
                ]} />

                {/* Ingredients */}
                <section className={sectionClass}>
                    <SectionTitle action={
                        <Button variant="tinted" size="sm" icon={Plus}
                                onClick={() => appendIngredient({ product_id: '', quantity: '', consumption_unit_id: '', loss_rate: 0, conversion_coefficient: 1 })}>
                            Ajouter
                        </Button>
                    }>
                        Ingrédients
                    </SectionTitle>

                    <div className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                        {ingredientFields.map((field, index) => (
                            <div key={field.id} className="py-3">
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <GripVertical className="w-4 h-4 text-gray-400 cursor-move flex-shrink-0" />

                                    <div className="flex-1 min-w-0">
                                        <button
                                            type="button"
                                            onClick={() => !isViewMode && setActivePicker(`ingredientProduct-${index}`)}
                                            disabled={isViewMode}
                                            className={pickerClass}
                                        >
                                            <span className={`truncate ${ingredientProducts[index] ? '' : 'text-gray-400'}`}>
                                                {ingredientProducts[index]?.name || 'Sélectionner un ingrédient'}
                                            </span>
                                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        </button>
                                        <input type="hidden" {...register(`ingredients.${index}.product_id`, { required: true })} />
                                        {errors?.ingredients?.[index]?.product_id && (
                                            <p className="text-red-500 text-xs mt-1">Un produit doit être sélectionné pour cet ingrédient</p>
                                        )}
                                    </div>

                                    <input
                                        type="number"
                                        step="0.01"
                                        placeholder="Quantité"
                                        {...register(`ingredients.${index}.quantity`, { required: true })}
                                        disabled={isViewMode}
                                        className={`${fieldClass} !w-24 sm:!w-28`}
                                    />

                                    <div className="w-24 sm:w-32">
                                        <button
                                            type="button"
                                            onClick={() => !isViewMode && setActivePicker(`ingredientUnit-${index}`)}
                                            disabled={isViewMode}
                                            className={pickerClass}
                                        >
                                            <span className={`truncate ${ingredientUnits[index] ? '' : 'text-gray-400'}`}>
                                                {ingredientUnits[index] ? (ingredientUnits[index].code || ingredientUnits[index].name) : 'Unité'}
                                            </span>
                                            <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                        </button>
                                        <input type="hidden" {...register(`ingredients.${index}.consumption_unit_id`)} />
                                    </div>

                                    {!isViewMode && (
                                        <Button variant="danger" size="sm" icon={Trash2} title="Supprimer l'ingrédient"
                                                onClick={() => handleRemoveIngredient(index)} />
                                    )}
                                </div>

                                {/* Rendement / conversion d'unité — ex: recette en g, stock en kg => coefficient 0.001 */}
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-2 ml-6 sm:ml-7">
                                    <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                        Perte (%)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        max="100"
                                        placeholder="0"
                                        {...register(`ingredients.${index}.loss_rate`)}
                                        disabled={isViewMode}
                                        className={`${smallFieldClass} w-24`}
                                    />
                                    <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                        Coefficient de conversion (unité recette → unité stock)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.000001"
                                        placeholder="1"
                                        {...register(`ingredients.${index}.conversion_coefficient`)}
                                        disabled={isViewMode}
                                        className={`${smallFieldClass} w-28`}
                                    />
                                    <span className="text-xs text-gray-400">Ex: recette en g, stock en kg → 0.001</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Sub-recipes */}
                <section className={sectionClass}>
                    <SectionTitle action={
                        <Button variant="tinted" size="sm" icon={Plus}
                                onClick={() => appendSubRecipe({ sub_recipe_id: '', quantity: '', unit_id: '' })}>
                            Ajouter une sous-recette
                        </Button>
                    }>
                        Sous-recettes
                    </SectionTitle>

                    {subRecipeFields.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Aucune sous-recette utilisée. Une sous-recette (ex : sauce tomate) doit d'abord être créée séparément avant de pouvoir être ajoutée ici.
                        </p>
                    )}

                    {subRecipeFields.length > 0 && (
                        <div className="divide-y divide-gray-200 dark:divide-gray-700 border-y border-gray-200 dark:border-gray-700">
                            {subRecipeFields.map((field, index) => {
                                const selectedSubRecipeId = watch(`sub_recipe_usages.${index}.sub_recipe_id`);
                                const selectedSubRecipe = availableSubRecipes.find(r => r.id === selectedSubRecipeId);
                                const yieldUnitLabel = selectedSubRecipe?.yieldUnit
                                    ? (selectedSubRecipe.yieldUnit.code || selectedSubRecipe.yieldUnit.name)
                                    : null;

                                return (
                                    <div key={field.id} className="py-3">
                                        <div className="flex items-center gap-2 sm:gap-3">
                                            <GripVertical className="w-4 h-4 text-gray-400 cursor-move flex-shrink-0" />

                                            <div className="flex-1 min-w-0">
                                                <AppleSelect
                                                    {...register(`sub_recipe_usages.${index}.sub_recipe_id`, {
                                                        required: true,
                                                        onChange: (e) => {
                                                            const sr = availableSubRecipes.find(r => r.id === e.target.value);
                                                            setValue(`sub_recipe_usages.${index}.unit_id`, extractId(sr?.yieldUnit) || '');
                                                        }
                                                    })}
                                                    disabled={isViewMode}
                                                    className={fieldClass}
                                                >
                                                    <option value="">Sélectionner une sous-recette</option>
                                                    {availableSubRecipes.map(sr => (
                                                        <option key={sr.id} value={sr.id}>{sr.name}</option>
                                                    ))}
                                                </AppleSelect>
                                            </div>

                                            <input
                                                type="number"
                                                step="0.0001"
                                                placeholder="Quantité"
                                                {...register(`sub_recipe_usages.${index}.quantity`, { required: true })}
                                                disabled={isViewMode}
                                                className={`${fieldClass} !w-24 sm:!w-28`}
                                            />

                                            <input type="hidden" {...register(`sub_recipe_usages.${index}.unit_id`)} />
                                            <div className="w-16 sm:w-24 py-2 sm:py-2.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 text-center">
                                                {yieldUnitLabel || '—'}
                                            </div>

                                            {!isViewMode && (
                                                <Button variant="danger" size="sm" icon={Trash2} title="Retirer la sous-recette"
                                                        onClick={() => removeSubRecipe(index)} />
                                            )}
                                        </div>
                                        {selectedSubRecipe && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6 sm:ml-7">
                                                Quantité exprimée dans l'unité de rendement de "{selectedSubRecipe.name}" ({yieldUnitLabel || 'non définie'}) — ex: 0.02 pour 20 g si le rendement est en kg.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Steps */}
                <section className="px-4 sm:px-6 py-5">
                    <SectionTitle action={
                        <Button variant="tinted" size="sm" icon={Plus}
                                onClick={() => appendStep({ order: stepFields.length + 1, description: '' })}>
                            Ajouter étape
                        </Button>
                    }>
                        Étapes de préparation
                    </SectionTitle>

                    <div className="space-y-3">
                        {stepFields.map((field, index) => (
                            <div key={field.id} className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-8 h-8 mt-1 rounded-full flex items-center justify-center text-sm font-bold bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary">
                                    {index + 1}
                                </div>

                                <textarea
                                    {...register(`steps.${index}.description`, { required: true })}
                                    disabled={isViewMode}
                                    rows={2}
                                    placeholder="Description de l'étape..."
                                    className={fieldClass}
                                />

                                {!isViewMode && (
                                    <Button variant="danger" size="sm" icon={Trash2} title="Supprimer l'étape" className="mt-1"
                                            onClick={() => removeStep(index)} />
                                )}
                            </div>
                        ))}
                    </div>
                </section>
            </form>
        </Modal>

            {activePicker === 'vendableProduct' && (
                <ReferencePickerModal
                    config={vendableProductsConfig}
                    currentValue={selectedVendableProduct?.id}
                    onSelect={(item) => {
                        setSelectedVendableProduct(item);
                        setValue('product_id', item.id, { shouldValidate: true });
                        setActivePicker(null);
                    }}
                    onClose={() => setActivePicker(null)}
                />
            )}

            {activePicker === 'yieldUnit' && (
                <ReferencePickerModal
                    config={unitsConfig}
                    currentValue={selectedYieldUnit?.id}
                    onSelect={(item) => {
                        setSelectedYieldUnit(item);
                        setValue('yield_unit_id', item.id, { shouldValidate: true });
                        setActivePicker(null);
                    }}
                    onClose={() => setActivePicker(null)}
                    displayValue={(item) => `${item.name} (${item.code || ''})`}
                />
            )}

            {typeof activePicker === 'string' && activePicker.startsWith('ingredientProduct-') && (
                <ReferencePickerModal
                    config={ingredientProductsConfig}
                    currentValue={ingredientProducts[Number(activePicker.split('-')[1])]?.id}
                    onSelect={(item) => {
                        const index = Number(activePicker.split('-')[1]);
                        setIngredientProducts(prev => ({ ...prev, [index]: item }));
                        setValue(`ingredients.${index}.product_id`, item.id, { shouldValidate: true });
                        setActivePicker(null);
                    }}
                    onClose={() => setActivePicker(null)}
                />
            )}

            {typeof activePicker === 'string' && activePicker.startsWith('ingredientUnit-') && (
                <ReferencePickerModal
                    config={unitsConfig}
                    currentValue={ingredientUnits[Number(activePicker.split('-')[1])]?.id}
                    onSelect={(item) => {
                        const index = Number(activePicker.split('-')[1]);
                        setIngredientUnits(prev => ({ ...prev, [index]: item }));
                        setValue(`ingredients.${index}.consumption_unit_id`, item.id, { shouldValidate: true });
                        setActivePicker(null);
                    }}
                    onClose={() => setActivePicker(null)}
                    displayValue={(item) => `${item.name} (${item.code || ''})`}
                />
            )}
        </>
    );
};

export default TechnicalFileForm;

